/**
 * Message Service
 * Handles message and conversation-related API calls
 */

import api from "@/lib/api";
import type { ApiResponse } from "@/lib/api";
import { toLocalDateString } from "@/lib/utils";
import type { Message } from "@/types/shadow.types";
import type { ConversationFilters } from "@/modules/shadow/components/conversation-filters";

export interface Contact {
  id: number;
  name: string;
  email?: string;
  phoneNumber?: string;
  identifier?: string;
}

export interface LastMessage {
  content: string;
  role: "user" | "assistant" | "agent";
  createdAt: string;
}

export interface ConversationSummary {
  conversationId: number;
  lastMessageAt: string | null;
  lastMessage: LastMessage | null;
  contact: Contact | null;
  categories: string[] | null;
  // Email-ticket subject (additional_attributes.subject); null for channels without one.
  // The backend already returns this on /messages/conversations.
  subject: string | null;
  channel: string | null;
}

export interface ConversationsResponse {
  conversations: ConversationSummary[];
  nextCursor: number | null;
  hasMore: boolean;
  totalCount: number;
}

export interface MessagesResponse {
  messages: Message[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** Query string shared by the two conversation listings. */
function conversationListParams(limit: number, cursor?: number, filters?: ConversationFilters): URLSearchParams {
  const params = new URLSearchParams();
  params.append("limit", limit.toString());
  if (cursor) {
    params.append("cursor", cursor.toString());
  }
  if (filters?.startDate) {
    params.append("startDate", toLocalDateString(filters.startDate));
  }
  if (filters?.endDate) {
    params.append("endDate", toLocalDateString(filters.endDate));
  }
  if (filters?.contactSearch) {
    params.append("contactSearch", filters.contactSearch);
  }
  if (filters?.conversationIdSearch) {
    params.append("conversationId", filters.conversationIdSearch);
  }
  if (filters?.ruleId) {
    params.append("ruleId", filters.ruleId);
  }
  if (filters?.rulesAndKbs && filters.rulesAndKbs.length > 0) {
    params.append("rulesAndKbs", JSON.stringify(filters.rulesAndKbs));
  }
  if (filters?.responseQuality && filters.responseQuality.length > 0) {
    filters.responseQuality.forEach((quality) => {
      params.append("responseQuality", quality);
    });
  }
  if (filters?.intents && filters.intents.length > 0) {
    filters.intents.forEach((intent) => {
      params.append("intents", intent);
    });
  }
  if (filters?.sortOrder) {
    params.append("sortOrder", filters.sortOrder);
  }
  return params;
}

export const messageService = {
  /**
   * Conversations with shadow metadata (with pagination and filters) — the AI Shadow Audit list.
   */
  async getConversationsWithShadow(
    limit: number = 20,
    cursor?: number,
    filters?: ConversationFilters,
    scope: { bu_id?: string } = {},
  ): Promise<ConversationsResponse> {
    const params = conversationListParams(limit, cursor, filters);
    if (scope.bu_id) params.append("bu_id", scope.bu_id);
    const response = await api.get<ApiResponse<ConversationsResponse>>(
      `/messages/conversations?${params.toString()}`,
    );
    return response.data.data;
  },

  /**
   * Every conversation of the account (not only shadow-evaluated ones), same filters and shape.
   * Used by the simulator to load any past conversation.
   */
  async getAllConversations(
    limit: number = 20,
    cursor?: number,
    filters?: ConversationFilters,
  ): Promise<ConversationsResponse> {
    const params = conversationListParams(limit, cursor, filters);
    const response = await api.get<ApiResponse<ConversationsResponse>>(
      `/messages/conversations/all?${params.toString()}`,
    );
    return response.data.data;
  },

  /**
   * Distinct detected intents on this account — the intent-filter options.
   */
  async getConversationIntents(): Promise<string[]> {
    const response = await api.get<ApiResponse<{ intents: string[] }>>(
      "/messages/conversations/intents",
    );
    return response.data.data.intents ?? [];
  },

  /**
   * Get messages for a conversation (with pagination)
   */
  async getConversationMessages(
    conversationId: number,
    limit: number = 50,
    cursor?: string,
  ): Promise<MessagesResponse> {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    if (cursor) {
      params.append("cursor", cursor);
    }
    const response = await api.get<ApiResponse<MessagesResponse>>(
      `/messages/conversations/${conversationId}?${params.toString()}`,
    );
    return response.data.data;
  },

  /**
   * Get rules and knowledge bases for filters
   */
  async getRulesAndKbs(): Promise<{
    rules: Array<{ id: string; name: string }>;
    knowledgeBases: Array<{ id: string; name: string }>;
  }> {
    const response = await api.get<
      ApiResponse<{
        rules: Array<{ id: string; name: string }>;
        knowledgeBases: Array<{ id: string; name: string }>;
      }>
    >("/messages/rules-and-kbs");
    return response.data.data;
  },
};
